import type { ServiceId, VehicleClassId } from '@/types'

import { garage } from '@/constants/garage'
import { SERVICES, VEHICLE_CLASSES } from '@/constants/pricing'

export type EstimateLine = {
    id: ServiceId
    name: string
    free: boolean
    ttc: number
    hours: number
    laborTtc: number
    partsTtc: number
}

export type Estimate = {
    vehicleClass: VehicleClassId
    lines: EstimateLine[]
    totalTtc: number
    totalHt: number
    vat: number
    hours: number
    turnaround: string
    reference: string
}

const round2 = (value: number): number => Math.round(value * 100) / 100

// Taux horaire TTC affiché sur le devis.
export const HOURLY_RATE_TTC = round2(garage.hourlyRate * (1 + garage.vatRate))

export const getService = (id: ServiceId) => SERVICES.find(service => service.id === id)

// Prix d'appel affiché dans la grille : le moins cher des gabarits.
export const getFromPrice = (id: ServiceId): number => {
    const service = getService(id)

    if (!service) return 0

    return Math.min(...Object.values(service.prices).map(price => price.ttc))
}

// Plaquettes seules et disques + plaquettes s'excluent : on garde le dernier choisi.
export const toggleService = (selected: ServiceId[], id: ServiceId): ServiceId[] => {
    if (selected.includes(id)) return selected.filter(item => item !== id)

    const group = getService(id)?.group
    const kept = group
        ? selected.filter(item => getService(item)?.group !== group)
        : selected

    return [...kept, id]
}

export const sanitizeServices = (ids: unknown): ServiceId[] => {
    if (!Array.isArray(ids)) return []

    return ids.reduce<ServiceId[]>((selected, raw) => {
        const id = String(raw) as ServiceId

        if (!getService(id) || selected.includes(id)) return selected

        return toggleService(selected, id)
    }, [])
}

export const isVehicleClass = (value: unknown): value is VehicleClassId => {
    return VEHICLE_CLASSES.some(item => item.id === value)
}

const getTurnaround = (hours: number, count: number): string => {
    if (count === 0) return 'Choisissez une intervention'
    if (hours <= 2.5) return 'Moins de 3 h, attente possible sur place'
    if (hours <= 6) return 'Rendue le jour même, dépôt avant 10 h'

    return 'Rendue sous 24 h'
}

export const computeEstimate = (vehicleClass: VehicleClassId, serviceIds: ServiceId[]): Estimate => {
    const lines = SERVICES
        .filter(service => serviceIds.includes(service.id))
        .map<EstimateLine>(service => {
            const { ttc, hours } = service.prices[vehicleClass]
            const laborTtc = service.free ? 0 : round2(hours * HOURLY_RATE_TTC)

            return {
                id: service.id,
                name: service.name,
                free: Boolean(service.free),
                ttc,
                hours,
                laborTtc,
                partsTtc: service.free ? 0 : round2(ttc - laborTtc)
            }
        })

    const totalTtc = lines.reduce((sum, line) => sum + line.ttc, 0)
    const totalHt = round2(totalTtc / (1 + garage.vatRate))
    const hours = round2(lines.reduce((sum, line) => sum + line.hours, 0))
    const classIndex = VEHICLE_CLASSES.findIndex(item => item.id === vehicleClass)
    const mask = lines.reduce((sum, line) => sum + 2 ** SERVICES.findIndex(service => service.id === line.id), 0)

    return {
        vehicleClass,
        lines,
        totalTtc,
        totalHt,
        vat: round2(totalTtc - totalHt),
        hours,
        turnaround: getTurnaround(hours, lines.length),
        reference: `E-${classIndex + 1}${mask.toString().padStart(3, '0')}`
    }
}
